import { FC } from "react";
import {
  VStack,
  FormControl,
  FormLabel,
  FormErrorMessage,
} from "@chakra-ui/react";
import { useFormikContext } from "formik";
import { CheckboxArrayItem } from "./CheckboxArrayItem";

interface CheckboxOption {
  value: string;
  label: string;
}

interface CheckboxGroupProps {
  name: string;
  label?: string;
  options: CheckboxOption[];
}

export const CheckboxGroup: FC<CheckboxGroupProps> = ({
  name,
  label,
  options,
}) => {
  const formik = useFormikContext<Record<string, unknown>>();
  const fieldValue = (formik.values[name] as string[] | undefined) || [];
  const fieldError = formik.errors[name];
  const fieldTouched = formik.touched[name];

  const handleCheckboxChange = (value: string, checked: boolean) => {
    const currentValues = fieldValue as string[];

    // Set touched first, but only on first interaction
    // The 'false' parameter prevents validation from running immediately
    if (!fieldTouched) {
      formik.setFieldTouched(name, true, false);
    }

    // Then update the value (which will trigger validation due to validateOnChange)
    if (checked) {
      formik.setFieldValue(name, [...currentValues, value]);
    } else {
      formik.setFieldValue(
        name,
        currentValues.filter((v) => v !== value),
      );
    }
  };

  return (
    <FormControl isInvalid={!!(fieldError && fieldTouched)}>
      {label && <FormLabel>{label}</FormLabel>}

      <VStack alignItems="flex-start" spacing={2}>
        {options.map((option) => (
          <CheckboxArrayItem
            key={option.value}
            fieldName={name}
            value={option.value}
            label={option.label}
            isChecked={fieldValue.includes(option.value)}
            onChange={(checked) => handleCheckboxChange(option.value, checked)}
          />
        ))}
      </VStack>

      {fieldError && fieldTouched && (
        <FormErrorMessage>{fieldError as string}</FormErrorMessage>
      )}
    </FormControl>
  );
};
